import type { ReferenceOption } from "@fieldpath/shared";
import { useEffect, useRef, useState } from "react";

/**
 * A note field where typing "@" offers people, and writing their name tags
 * them.
 *
 * The mentions are DERIVED FROM THE TEXT rather than held in a list beside
 * it. That is the whole point: what you tagged is what the note says, so
 * deleting "@Jacob Weiss" untags him and nobody has to remember to also
 * uncheck a box. A separate list can disagree with the note; this cannot.
 *
 * Names are matched longest-first so "Jacob Weiss" wins over any "Jacob", and
 * an "@" only counts at the start of a word — an email address in a note is
 * not four mentions.
 */

/** How many words a name may span while still being offered as a match. */
const MAX_NAME_WORDS = 3;

export function mentionsInText(
  text: string,
  people: readonly ReferenceOption[],
): string[] {
  const found = new Set<string>();
  const lower = text.toLowerCase();

  // Longest first: otherwise a short name that prefixes a longer one would
  // match first and stop the search.
  const byLength = [...people].sort((a, b) => b.label.length - a.label.length);

  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== "@") continue;
    // Must begin a word. Stops "name@example.com" being read as a mention.
    if (i > 0 && !/\s/.test(text[i - 1] ?? "")) continue;

    const after = lower.slice(i + 1);
    for (const p of byLength) {
      const name = p.label.toLowerCase();
      if (name && after.startsWith(name)) {
        found.add(p.key);
        break;
      }
    }
  }

  return [...found];
}

/** The "@word" being typed at the caret, if there is one. */
function activeQuery(
  text: string,
  caret: number,
): { at: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;

  // Must start a word, same rule as above.
  if (at > 0 && !/\s/.test(before[at - 1] ?? "")) return null;

  const query = before.slice(at + 1);
  // A newline ends it, and so does running past a plausible name length —
  // otherwise the menu would hang around for the rest of the paragraph.
  if (query.includes("\n")) return null;
  if (query.split(/\s+/).length > MAX_NAME_WORDS) return null;

  return { at, query };
}

export function MentionBox({
  value,
  people,
  onChange,
  placeholder,
}: {
  value: string;
  people: readonly ReferenceOption[];
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const active = activeQuery(value, caret);
  const query = active?.query.trim().toLowerCase() ?? "";

  // An exact name means the tag is already made; keeping the menu open over
  // a finished mention is just something else to dismiss.
  const exact = people.some((p) => p.label.toLowerCase() === query);

  // Initials too, as in Wrike (punch list #58, 20 Oct 2026): "@jl" or
  // "@jloe" is the first name's initial followed by the start of a later
  // name, so it offers Jennifer Loeffler. Name matches are listed first.
  const byInitial = (label: string): boolean => {
    if (query.length < 2 || /\s/.test(query)) return false;
    const words = label.toLowerCase().split(/\s+/);
    return words[0]!.startsWith(query[0]!) && words.slice(1).some((w) => w.startsWith(query.slice(1)));
  };
  const matches =
    active && !dismissed && !exact
      ? [
          ...people.filter((p) => p.label.toLowerCase().includes(query)),
          ...people.filter((p) => !p.label.toLowerCase().includes(query) && byInitial(p.label)),
        ].slice(0, 6)
      : [];

  useEffect(() => setHighlight(0), [query]);

  const choose = (person: ReferenceOption): void => {
    if (!active) return;
    const before = value.slice(0, active.at);
    const after = value.slice(caret);
    // Trailing space so the next word is not swallowed into the name, and so
    // the tag reads as finished.
    const inserted = `@${person.label} `;
    const next = `${before}${inserted}${after}`;
    onChange(next);

    const pos = active.at + inserted.length;
    // After React has written the new value, or the caret lands in the old
    // text and jumps somewhere surprising.
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (matches.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      // Enter picks the highlighted person rather than breaking the line.
      // Only while the menu is open, so ordinary typing is unaffected.
      e.preventDefault();
      const person = matches[highlight];
      if (person) choose(person);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setDismissed(true);
    }
  };

  const sync = (el: HTMLTextAreaElement): void => {
    setCaret(el.selectionStart ?? 0);
    setDismissed(false);
  };

  return (
    <div className="mention-wrap">
      <textarea
        ref={ref}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          sync(e.target);
        }}
        onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={onKeyDown}
        // A blur that is a click on the menu must not close it first.
        onBlur={() => window.setTimeout(() => setDismissed(true), 150)}
      />

      {matches.length > 0 ? (
        <ul className="mention-menu">
          {matches.map((p, i) => (
            <li key={p.key}>
              <button
                type="button"
                className={i === highlight ? "on" : ""}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(p)}
              >
                {p.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
